import { Badge, Box, Group, Skeleton, Text } from '@mantine/core';
import type { FormView, LocationInfo } from '@/api';

function formatTimeDifference(viewTime: string, messageTime: string): string {
  const viewDate = new Date(viewTime);
  const messageDate = new Date(messageTime);
  const diffMs = messageDate.getTime() - viewDate.getTime();

  if (diffMs < 0) {
    return 'Message submitted before view (unusual)';
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} after view`;
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} after view`;
  }
  if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} after view`;
  }
  return `${diffSeconds} second${diffSeconds > 1 ? 's' : ''} after view`;
}

function LocationDetails({ location }: { location?: LocationInfo }) {
  if (!location) {
    return (
      <Text size="xs" c="dimmed">
        Unknown
      </Text>
    );
  }

  const locationParts = [
    location.city,
    location.region_name,
    location.country_name || location.country_code,
  ].filter(Boolean);

  return (
    <Box>
      <Text size="xs" c="dimmed">
        Location:{' '}
        <Text span fw={500}>
          {locationParts.join(', ') || 'Unknown'}
        </Text>
      </Text>
      {location.timezone && (
        <Text size="xs" c="dimmed">
          Timezone:{' '}
          <Text span fw={500}>
            {location.timezone}
          </Text>
        </Text>
      )}
      {location.latitude && location.longitude && (
        <Text size="xs" c="dimmed">
          Coordinates:{' '}
          <Text span fw={500}>
            {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
          </Text>
        </Text>
      )}
    </Box>
  );
}

// Shaped like the loaded panel, so the row settles at its final height on
// expand rather than jumping when the fetch lands.
function ViewAnalyticsSkeleton() {
  return (
    <Box>
      <Group gap="md" mb="xs">
        <Skeleton height={20} width={110} radius="sm" />
        <Skeleton height={20} width={125} radius="sm" />
        <Skeleton height={20} width={45} radius="sm" />
      </Group>
      <Skeleton height={11} width="42%" mb={7} />
      <Skeleton height={11} width="28%" mb="sm" />
      <Skeleton height={11} width="18%" mb={7} />
      <Skeleton height={11} width="52%" mb={7} />
      <Skeleton height={11} width="34%" />
    </Box>
  );
}

export interface ViewAnalyticsProps {
  /** `undefined` while the fetch is outstanding, `null` when no view matched. */
  view: FormView | null | undefined;
  loading: boolean;
  messageCreatedAt: string;
  location?: LocationInfo;
}

export function ViewAnalytics({ view, loading, messageCreatedAt, location }: ViewAnalyticsProps) {
  return (
    <Box mt="md">
      <Text size="sm" fw={500} mb="xs">
        View Analytics:
      </Text>
      {view === undefined || loading ? (
        <ViewAnalyticsSkeleton />
      ) : view === null ? (
        <Text size="sm" c="dimmed">
          No associated form view found
        </Text>
      ) : (
        <Box>
          <Group gap="md" mb="xs">
            <Badge color="blue" variant="light">
              {view.device_type || 'Unknown'} Device
            </Badge>
            <Badge color="green" variant="light">
              {view.browser_name || 'Unknown'} Browser
            </Badge>
            {view.location?.country_code && (
              <Badge color="orange" variant="light">
                {view.location.country_code}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            Form submitted{' '}
            <Text span fw={500}>
              {formatTimeDifference(view.created_at, messageCreatedAt)}
            </Text>
          </Text>
          {view.traffic_source && (
            <Text size="xs" c="dimmed">
              Traffic source:{' '}
              <Text span fw={500}>
                {view.traffic_source}
              </Text>
            </Text>
          )}
          <Box mb="sm" mt="sm">
            <Text size="xs" fw={500} c="orange">
              Location:
            </Text>
            <LocationDetails location={location} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
